import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Cull } from '../types';
import { cullsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Map.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapProps {
  selectedCull?: Cull | null;
  onCullSelect?: (cull: Cull) => void;
}

const Map: React.FC<MapProps> = ({ selectedCull, onCullSelect }) => {
  const { user, isAuthenticated } = useAuth();
  const [culls, setCulls] = useState<Cull[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCulls = async () => {
      if (!isAuthenticated || !user?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Show user's own records
        const data = await cullsApi.getUserCulls(user.uid, { limit: 1000 });
        console.log('Fetched culls:', data.length);
        setCulls(data.filter(cull => cull.latitude && cull.longitude));
      } catch (error) {
        console.error('Error fetching culls:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCulls();
  }, [isAuthenticated, user]);

  const englandCenter: [number, number] = [52.5, -1.5];
  const defaultZoom = 6;

  if (loading) {
    return <div className="map-loading">Loading map...</div>;
  }

  return (
    <div className="map-container">
      {!isAuthenticated && (
        <div className="map-empty-message">
          <p>Please log in to view your cull records on the map.</p>
          <p><small>Cull data is private and only visible to authenticated users.</small></p>
          <Link to="/login" className="map-login-btn">Login to View Records</Link>
        </div>
      )}
      {isAuthenticated && culls.length === 0 && !loading && (
        <div className="map-empty-message">
          <p>No cull records with location data found.</p>
          <p><small>Add records with GPS coordinates to see them on the map.</small></p>
        </div>
      )}
      <MapContainer
        center={englandCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {culls.map((cull) => (
          <Marker
            key={cull.id}
            position={[cull.latitude!, cull.longitude!]}
            eventHandlers={{
              click: () => onCullSelect?.(cull),
            }}
          >
            <Popup>
              <div className="popup-content">
                <h3>{cull.location_name}</h3>
                <div className="popup-details">
                  <p><strong>Date:</strong> {cull.date}</p>
                  <p><strong>Time:</strong> {cull.time}</p>
                  <p><strong>Species:</strong> {cull.species}</p>
                  <p><strong>Sex:</strong> {cull.sex}</p>
                  <p><strong>Age:</strong> {cull.age_estimate}</p>
                  {cull.weight && <p><strong>Weight:</strong> {cull.weight} kg</p>}
                  <p><strong>Condition:</strong> {cull.condition}</p>
                  {cull.antlers_present && (
                    <p><strong>Antlers:</strong> {cull.antler_points} points ({cull.antler_condition})</p>
                  )}
                  <p><strong>Shot Placement:</strong> {cull.shot_placement}</p>
                  {cull.shot_distance && <p><strong>Distance:</strong> {cull.shot_distance}</p>}
                  {cull.notes && <p><strong>Notes:</strong> {cull.notes}</p>}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default Map;